import PropTypes from 'prop-types'
import { format, toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import { ZoomFrame } from '@/components/ui/zoomable-image'
import ModalContainer from '../../ModalContainer/ModelContainer'
import Icon from '../../Icons'
import { formatMoney } from '../../../helpers/helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'

const withNoValue = (string) => string || '-'

const DetailsModal = ({ ticket, onClose, timezone }) => {
  const { t } = useTranslation(['parking-dashboard'])
  const dateStart = toZonedTime(new Date(ticket.startDate), timezone)
  const dateEnd = toZonedTime(new Date(ticket.endDate), timezone)

  const onDownloadImage = async (photo) => {
    const imageUrl = getAssetLink({ path: 'vehicle-images', file: photo })
    const image = await fetch(imageUrl)
    const imageBlog = await image.blob()
    const imageURL = URL.createObjectURL(imageBlog)

    const link = document.createElement('a')
    link.href = imageURL
    link.download = photo
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <ModalContainer
      additionalClass="parking-dashboard-modal"
      onClose={onClose}
      headerTitle={t('parking-dashboard:detailsModal.title')}
    >
      <div>
        <div className="parking-dashboard-modal__section">
          <div className="parking-dashboard-modal__section--header">
            {t('parking-dashboard:detailsModal.visitorInformation')}
          </div>
          <div>
            {ticket.visitorName && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.visitorName')}</div>
                <div>{withNoValue(ticket.visitorName)}</div>
              </div>
            )}
            {ticket.phone && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.visitorPhone')}</div>
                <div>{withNoValue(ticket.phone)}</div>
              </div>
            )}
          </div>
        </div>
        <div className="parking-dashboard-modal__section">
          <div className="parking-dashboard-modal__section--header">
            {t('parking-dashboard:detailsModal.vehicleDetails')}
          </div>
          <div>
            {ticket.licensePlate && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.licensePlateNumber')}</div>
                <div>{withNoValue(ticket.licensePlate)}</div>
              </div>
            )}
            {ticket.color && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.color')}</div>
                <div>{withNoValue(ticket.color)}</div>
              </div>
            )}
            {ticket.make && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.make')}</div>
                <div>{withNoValue(ticket.make)}</div>
              </div>
            )}
            {ticket.model && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.model')}</div>
                <div>{withNoValue(ticket.model)}</div>
              </div>
            )}
            {ticket.photo && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.vehiclePhoto')}</div>
                <div>
                  <a
                    href={getAssetLink({ path: 'vehicle-images', file: ticket.photo })}
                    target="__blank"
                    style={{ color: '#9B7A57' }}
                  >
                    {t('parking-dashboard:detailsModal.photoFileName')}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="parking-dashboard-modal__section">
          <div className="parking-dashboard-modal__section--header">
            {t('parking-dashboard:detailsModal.serviceDetails')}
          </div>
          <div>
            {ticket.ticketNumber && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.ticketNo')}</div>
                <div>{withNoValue(ticket.ticketNumber)}</div>
              </div>
            )}
            {ticket.startDate && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.startDateTime')}</div>
                <div>{ticket.startDate ? format(dateStart, 'MM/dd/yyyy hh:mm a') : '-'}</div>
              </div>
            )}
            {ticket.endDate && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.endDateTime')}</div>
                <div>{ticket.endDate ? format(dateEnd, 'MM/dd/yyyy hh:mm a') : '-'}</div>
              </div>
            )}
            {ticket.serviceFee && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:columns.serviceFee')}</div>
                <div>{withNoValue(formatMoney(ticket.serviceFee / 100))}</div>
              </div>
            )}
            {ticket.tip && (
              <div className="parking-dashboard-modal__section--row">
                <div>{t('parking-dashboard:detailsModal.tip')}</div>
                <div>{withNoValue(formatMoney(ticket.tip / 100))}</div>
              </div>
            )}
          </div>
        </div>
        {ticket.ticketPhotos && ticket.ticketPhotos.length > 0 ? (
          <div className="parking-dashboard-modal__section">
            <div className="parking-dashboard-modal__section--header">
              {t('parking-dashboard:detailsModal.vehicleCondition')}
            </div>
            <div className="parking-dashboard-modal__section--photos-container">
              {ticket.ticketPhotos.map(({ photo, createdAt }, index) => (
                <div
                  key={photo}
                  className="parking-dashboard-modal__section--photos-container--photo"
                >
                  <ZoomFrame
                    className="photo-frame"
                    fill
                    src={getAssetLink({ path: 'vehicle-images', file: photo })}
                    title={t('parking-dashboard:detailsModal.vehicleCondition')}
                    alt={photo}
                    images={ticket.ticketPhotos.map((item) =>
                      getAssetLink({ path: 'vehicle-images', file: item.photo }),
                    )}
                    initialIndex={index}
                  >
                    <img src={getAssetLink({ path: 'vehicle-images', file: photo })} alt={photo} />
                  </ZoomFrame>
                  <span className="photo-time">
                    {format(toZonedTime(createdAt, timezone), 'hh:mm a MM/dd/yyyy')}
                  </span>
                  <div onClick={() => onDownloadImage(photo)}>
                    <Icon
                      icon="arrowDownload"
                      fill="none"
                      viewBox="-2 -2 20 20"
                      svgProps={{
                        strokeWidth: '1.81824',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </ModalContainer>
  )
}

DetailsModal.propTypes = {
  ticket: PropTypes.object,
  onClose: PropTypes.func,
  timezone: PropTypes.string,
}

export default DetailsModal
